// Custom hook for managing series with Firestore
import { useState, useEffect } from 'react';
import { Series } from '../types';
import {
  getAllSeries,
  getSeriesById,
  getSeriesByLender,
  createSeries,
  updateSeries,
  deleteSeries,
  subscribeSeries,
  subscribeLenderSeries,
  getActiveSeries,
  getRedeemableSeries,
  calculateTotalAUM,
} from '../services/seriesService';

export const useSeries = (realTime: boolean = false) => {
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realTime) {
      // Subscribe to real-time updates
      const unsubscribe = subscribeSeries(
        (updatedSeries) => {
          setSeries(updatedSeries);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // Fetch once
      fetchSeries();
    }
  }, [realTime]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const data = await getAllSeries();
      setSeries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addSeries = async (seriesData: Omit<Series, 'id'>) => {
    try {
      const id = await createSeries(seriesData);
      if (!realTime) {
        await fetchSeries();
      }
      return id;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const editSeries = async (seriesId: string, updates: Partial<Omit<Series, 'id'>>) => {
    try {
      await updateSeries(seriesId, updates);
      if (!realTime) {
        await fetchSeries();
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const removeSeries = async (seriesId: string) => {
    try {
      await deleteSeries(seriesId);
      if (!realTime) {
        await fetchSeries();
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const refresh = () => {
    fetchSeries();
  };

  return {
    series,
    loading,
    error,
    addSeries,
    editSeries,
    removeSeries,
    refresh,
  };
};

export const useSeriesByLender = (lenderId: string, realTime: boolean = false) => {
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realTime) {
      // Subscribe to real-time updates
      const unsubscribe = subscribeLenderSeries(
        lenderId,
        (updatedSeries) => {
          setSeries(updatedSeries);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // Fetch once
      fetchSeries();
    }
  }, [lenderId, realTime]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const data = await getSeriesByLender(lenderId);
      setSeries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    series,
    loading,
    error,
    refresh: fetchSeries,
  };
};

export const useSeriesDetails = (seriesId: string) => {
  const [series, setSeriesDetails] = useState<Series | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSeriesDetails();
  }, [seriesId]);

  const fetchSeriesDetails = async () => {
    try {
      setLoading(true);
      const data = await getSeriesById(seriesId);
      setSeriesDetails(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    series,
    loading,
    error,
    refresh: fetchSeriesDetails,
  };
};

export const useActiveSeries = () => {
  const [activeSeries, setActiveSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchActiveSeries();
  }, []);

  const fetchActiveSeries = async () => {
    try {
      setLoading(true);
      const data = await getActiveSeries();
      setActiveSeries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    activeSeries,
    loading,
    error,
    refresh: fetchActiveSeries,
  };
};

export const useRedeemableSeries = () => {
  const [redeemableSeries, setRedeemableSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRedeemableSeries();
  }, []);

  const fetchRedeemableSeries = async () => {
    try {
      setLoading(true);
      const data = await getRedeemableSeries();
      setRedeemableSeries(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    redeemableSeries,
    loading,
    error,
    refresh: fetchRedeemableSeries,
  };
};

export const useTotalAUM = () => {
  const [totalAUM, setTotalAUM] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTotalAUM();
  }, []);

  const fetchTotalAUM = async () => {
    try {
      setLoading(true);
      const aum = await calculateTotalAUM();
      setTotalAUM(aum);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    totalAUM,
    loading,
    error,
    refresh: fetchTotalAUM,
  };
};
